import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatPersistentEditValue } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import { useDismissAiScriptCellOverride } from "@/payroll/ai-script/query";
import type { IAiSpreadsheetCellOverride } from "@/payroll/ai-script/types";
import { InlineConfirmation } from "./inline-confirmation";
import { showPersistentEditMutationError } from "./shared";

interface OriginalValueEntryProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  canResolve: boolean | null;
  onResolved: () => void;
  onWorkbookMutated?: () => void;
}

export const OriginalValueEntry = ({
  override,
  businessId,
  runId,
  canResolve,
  onResolved,
  onWorkbookMutated,
}: OriginalValueEntryProps) => {
  const dismiss = useDismissAiScriptCellOverride(businessId);
  const [isConfirming, setIsConfirming] = useState(false);

  const confirmRevert = () =>
    dismiss.mutate(
      { runId, overrideId: override.id },
      {
        onSuccess: () => {
          setIsConfirming(false);
          onWorkbookMutated?.();
          onResolved();
        },
        onError: (error) => {
          setIsConfirming(false);
          if (
            showPersistentEditMutationError("Couldn't discard this edit", error)
          ) {
            onResolved();
          }
        },
      },
    );

  return (
    <Stack
      gap={1}
      align="stretch"
      className="w-full rounded-xl border border-dashed border-line p-4"
    >
      <Stack
        className="w-full"
        direction="row"
        gap={2}
        align="center"
        justify="between"
      >
        <Stack align="start" gap={0} className="min-w-0 flex-1">
          <Text
            className="min-w-0 truncate font-semibold"
            title={formatPersistentEditValue(override.previous_value)}
          >
            {formatPersistentEditValue(override.previous_value)}
          </Text>
          <Text variant="caption" tone="muted">
            Generated by the payroll logic
          </Text>
        </Stack>
        {canResolve && !isConfirming ? (
          <Button
            variant="outline"
            size="xs"
            onClick={() => setIsConfirming(true)}
            disabled={dismiss.isPending}
            aria-busy={dismiss.isPending || undefined}
          >
            {dismiss.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Revert to this
          </Button>
        ) : (
          <Badge variant="secondary" size="sm">
            Original
          </Badge>
        )}
      </Stack>
      {isConfirming ? (
        <InlineConfirmation
          message={
            <>
              Go back to the script&apos;s value? This removes your edit, so the
              cell follows the payroll logic again on every run.
            </>
          }
          confirmLabel="Revert to original"
          isPending={dismiss.isPending}
          onCancel={() => setIsConfirming(false)}
          onConfirm={confirmRevert}
        />
      ) : null}
    </Stack>
  );
};
